import type { Json } from "@/lib/database.types";
import { buildNearMeListingAlertLineFromItem } from "@/lib/notifications/near-me-alert-copy";
import { buildConfirmedAvailableDigestLine } from "@/lib/notifications/near-me-confirmed-available-copy";
import { appBaseUrlForDeepLinks } from "@/lib/whatsapp/app-base-url-for-deep-links";
import { sanitizeWhatsappParamText } from "@/lib/whatsapp/whatsapp-param-safety";

export const NEAR_ME_DIGEST_EVENT_TYPE = "new_listing_near_me_digest";

export const NEAR_ME_DIGEST_INBOX_TITLE = "New listings near you";

const DIGEST_TITLE = NEAR_ME_DIGEST_INBOX_TITLE;

export function isNearMeDigestNotification(eventType?: string | null): boolean {
  return eventType === NEAR_ME_DIGEST_EVENT_TYPE;
}

const LIMITS = {
  inAppBodyLines: 8,
  inAppPreviewLines: 1,
  pushLines: 2,
  pushMaxBodyChars: 200,
  emailLines: 10,
  whatsappLines: 5,
  whatsappSummaryMaxChars: 900,
} as const;

export type NearMeDigestPendingReason = "new_listing" | "confirmed_available";

export type NearMeDigestItemSnapshot = {
  itemId: string;
  title: string;
  location: string | null;
  price: number | null;
  /** Item subcategory id — used to gate the WhatsApp digest by the auto-send allowlist. */
  subcategory?: string | null;
};

export type NearMeDigestLine = {
  itemId: string;
  alertLine: string;
  title?: string;
  location?: string | null;
  price?: number | null;
};

export type NearMeDigestInboxMetadata = {
  version: 1;
  totalCount: number;
  overflowCount: number;
  lines: NearMeDigestLine[];
};

export type NearMeDigestEmailLine = NearMeDigestLine & {
  itemUrl: string;
};

/** Primary tap target for digest inbox row, push, and bell dropdown (first listing). */
export function resolveNearMeDigestPrimaryItemUrl(
  lines: readonly Pick<NearMeDigestLine, "itemId">[],
): string {
  const firstId = lines[0]?.itemId?.trim();
  if (firstId) return `/item/${firstId}`;
  return "/market";
}

/**
 * Prefer item deep link from inbox metadata (fixes legacy rows stored with `/market`).
 */
export function resolveNearMeDigestClickUrl(
  metadata: unknown,
  storedUrl?: string | null,
): string {
  const parsed = parseNearMeDigestInboxMetadata(metadata);
  if (parsed?.lines[0]?.itemId) {
    return `/item/${parsed.lines[0].itemId}`;
  }
  const trimmed = storedUrl?.trim();
  if (trimmed) return trimmed;
  return "/market";
}

export function snapshotToDigestLine(
  snapshot: NearMeDigestItemSnapshot,
  reason: NearMeDigestPendingReason = "new_listing",
): NearMeDigestLine {
  if (reason === "confirmed_available") {
    return {
      itemId: snapshot.itemId,
      alertLine: buildConfirmedAvailableDigestLine({
        itemTitle: snapshot.title,
        itemPrice: snapshot.price,
      }),
      title: snapshot.title,
      location: snapshot.location,
      price: snapshot.price,
    };
  }
  return {
    itemId: snapshot.itemId,
    alertLine: buildNearMeListingAlertLineFromItem({
      itemTitle: snapshot.title,
      itemLocation: snapshot.location,
      itemPrice: snapshot.price,
    }),
    title: snapshot.title,
    location: snapshot.location,
    price: snapshot.price,
  };
}

export function snapshotsToDigestLines(
  snapshots: NearMeDigestItemSnapshot[],
): NearMeDigestLine[] {
  return snapshots.map((snapshot) =>
    snapshotToDigestLine(snapshot, "new_listing"),
  );
}

export function snapshotsToDigestLinesWithReason(
  entries: Array<{
    snapshot: NearMeDigestItemSnapshot;
    reason: NearMeDigestPendingReason;
  }>,
): NearMeDigestLine[] {
  return entries.map(({ snapshot, reason }) =>
    snapshotToDigestLine(snapshot, reason),
  );
}

export function buildNearMeDigestInboxMetadata(
  lines: NearMeDigestLine[],
): NearMeDigestInboxMetadata {
  const totalCount = lines.length;
  const storedLines = lines.slice(0, LIMITS.inAppBodyLines);
  return {
    version: 1,
    totalCount,
    overflowCount: Math.max(0, totalCount - storedLines.length),
    lines: storedLines,
  };
}

export function nearMeDigestInboxMetadataToJson(
  metadata: NearMeDigestInboxMetadata,
): Json {
  return metadata as unknown as Json;
}

export function parseNearMeDigestInboxMetadata(
  metadata: unknown,
): NearMeDigestInboxMetadata | null {
  if (!metadata || typeof metadata !== "object") return null;
  const row = metadata as Record<string, unknown>;
  if (row.version !== 1) return null;
  if (
    typeof row.totalCount !== "number" ||
    typeof row.overflowCount !== "number"
  ) {
    return null;
  }
  if (!Array.isArray(row.lines)) return null;

  const lines: NearMeDigestLine[] = [];
  for (const entry of row.lines) {
    if (!entry || typeof entry !== "object") continue;
    const line = entry as Record<string, unknown>;
    if (typeof line.itemId !== "string" || typeof line.alertLine !== "string") {
      continue;
    }
    lines.push({ itemId: line.itemId, alertLine: line.alertLine });
  }

  if (lines.length === 0) return null;

  return {
    version: 1,
    totalCount: row.totalCount,
    overflowCount: row.overflowCount,
    lines,
  };
}

function joinPreviewLines(
  lines: string[],
  maxLines: number,
  separator: string,
): string {
  return lines.slice(0, maxLines).join(separator);
}

function buildOverflowSuffix(
  count: number,
  channel: "in_app" | "push",
): string {
  if (count <= 0) return "";
  if (channel === "in_app") {
    return ` · +${count} more today. Tap to browse.`;
  }
  return ` · +${count} more`;
}

export function buildNearMeDigestInAppCopy(lines: NearMeDigestLine[]): {
  title: string;
  body: string;
  miniBody: string;
  metadata: NearMeDigestInboxMetadata;
} {
  const alertLines = lines.map((line) => line.alertLine);
  const metadata = buildNearMeDigestInboxMetadata(lines);
  const bodyLines = alertLines.slice(0, LIMITS.inAppBodyLines);
  let body = bodyLines.join("\n");
  if (metadata.overflowCount > 0) {
    body += `\n+${metadata.overflowCount} more on the marketplace.`;
  }

  let miniBody: string;
  if (alertLines.length === 0) {
    miniBody = "New listings are live near you on Sell It Off.";
  } else if (alertLines.length === 1) {
    miniBody = alertLines[0];
  } else {
    const preview = joinPreviewLines(
      alertLines,
      LIMITS.inAppPreviewLines,
      " · ",
    );
    miniBody = `${preview}${buildOverflowSuffix(alertLines.length - LIMITS.inAppPreviewLines, "in_app")}`;
  }

  return {
    title: DIGEST_TITLE,
    body,
    miniBody,
    metadata,
  };
}

export function buildNearMeDigestPushCopy(lines: NearMeDigestLine[]): {
  title: string;
  body: string;
} {
  const alertLines = lines.map((line) => line.alertLine);
  if (alertLines.length === 0) {
    return {
      title: DIGEST_TITLE,
      body: "New listings are live near you on Sell It Off.",
    };
  }

  if (alertLines.length === 1) {
    return { title: DIGEST_TITLE, body: alertLines[0] };
  }

  const tryTwoLines = () => {
    const shown = alertLines.slice(0, LIMITS.pushLines);
    const overflow = alertLines.length - shown.length;
    const base = shown.join(" · ");
    if (overflow <= 0) return base;
    return `${base}${buildOverflowSuffix(overflow, "push")}`;
  };

  let body = tryTwoLines();
  if (body.length > LIMITS.pushMaxBodyChars) {
    const oneLine = alertLines[0];
    const overflow = alertLines.length - 1;
    body =
      overflow > 0
        ? `${oneLine}${buildOverflowSuffix(overflow, "push")}`
        : oneLine;
  }

  return { title: DIGEST_TITLE, body };
}

export function buildNearMeDigestEmailSubject(totalCount: number): string {
  return `${totalCount} new listing${totalCount === 1 ? "" : "s"} near you today`;
}

/**
 * WhatsApp template params reject newlines/tabs/>4 spaces (Meta 132018), so digest
 * listing lines are joined with a mid-dot separator instead of `\n` (unlike in-app/email).
 */
const WHATSAPP_DIGEST_LINE_SEPARATOR = " · ";

function buildWhatsappOverflowSuffix(count: number): string {
  if (count <= 0) return "";
  return `${WHATSAPP_DIGEST_LINE_SEPARATOR}+${count} more on Sell It Off`;
}

/** List-first digest summary for WhatsApp template `digestsummary` (max 5 lines + overflow). */
export function buildNearMeDigestWhatsappCopy(
  lines: NearMeDigestLine[],
  baseUrl?: string,
): {
  listingCount: string;
  digestSummary: string;
  marketLink: string;
} {
  const totalCount = lines.length;
  const shown = lines.slice(0, LIMITS.whatsappLines);
  const overflowCount = Math.max(0, totalCount - shown.length);
  let digestSummary = shown
    .map((line) => sanitizeWhatsappParamText(line.alertLine))
    .join(WHATSAPP_DIGEST_LINE_SEPARATOR);
  if (overflowCount > 0) {
    digestSummary += buildWhatsappOverflowSuffix(overflowCount);
  }
  if (digestSummary.length > LIMITS.whatsappSummaryMaxChars) {
    digestSummary = `${digestSummary.slice(0, LIMITS.whatsappSummaryMaxChars - 1).trimEnd()}…`;
  }

  const marketBase = (baseUrl ?? appBaseUrlForDeepLinks()).replace(/\/$/, "");
  return {
    listingCount: String(totalCount),
    digestSummary,
    marketLink: `${marketBase}/market`,
  };
}

export function buildNearMeDigestEmailContent(
  lines: NearMeDigestLine[],
  baseUrl: string,
): {
  subject: string;
  listingLines: NearMeDigestEmailLine[];
  overflowCount: number;
  plainBodyFallback: string;
} {
  const shown = lines.slice(0, LIMITS.emailLines);
  const overflowCount = Math.max(0, lines.length - shown.length);
  const listingLines = shown.map((line) => ({
    ...line,
    itemUrl: `${baseUrl.replace(/\/$/, "")}/item/${line.itemId}`,
  }));

  let plainBodyFallback = shown.map((line) => line.alertLine).join("\n");
  if (overflowCount > 0) {
    plainBodyFallback += `\n+${overflowCount} more on the marketplace.`;
  }

  return {
    subject: buildNearMeDigestEmailSubject(lines.length),
    listingLines,
    overflowCount,
    plainBodyFallback,
  };
}

/** @deprecated Use channel-specific near-me digest copy builders. */
export function buildDigestBody(titles: string[]): string {
  const lines = titles.map((title, index) => ({
    itemId: `legacy-${index}`,
    alertLine: title.trim() || "Listing",
  }));
  return buildNearMeDigestInAppCopy(lines).body;
}
